import { yupResolver } from '@hookform/resolvers/yup';
import { Headline, Text } from '@velocity/ui';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { deleteCustomerDocument } from '../../../../redux/leaseApp/lease-app-slice';
import { useAppDispatch } from '../../../../redux/store';
import { RootState } from '../../../../redux/types';
import {
  IdentityDocuments,
  IdentityDocumentType,
  PrivateCustomer,
} from '../../../../types/instant-lease-api';
import {
  getApplicantIncomeDocuments,
  getCoApplicant,
  getDocumentsCount,
  getMainApplicant,
} from '../../../../utils/getter';
import ButtonGroup from '../../../b2b/molecules/button-group/button-group';
import { FormRadioField } from '../../../common/velocity-form';
import UploadBoxB2C from '../../components/upload-box-b2c/upload-box-b2c';

export interface DocumentDataSubmittedValues {
  identity_choice: IdentityDocumentType;
  identity_number?: string;
  document_id?: string;
}
interface DocumentDataProps {
  onSubmit: (
    person: DocumentDataSubmittedValues,
    goNext: boolean,
    document: string,
  ) => void;
  goBack: () => void;
  privateCustomer: PrivateCustomer | undefined;
  coApplicant: boolean;
  loading?: boolean;
  setLoading?: (loading?: boolean) => void;
}

const DocumentTemplate: React.FC<DocumentDataProps> = ({
  // goBack,
  privateCustomer,
  onSubmit,
  coApplicant,
  loading,
  setLoading,
}) => {
  // Lease Application Storage
  const { activeApplication } = useSelector(
    (state: RootState) => state.leaseApp,
  );

  // Auth Storage
  const { accessToken } = useSelector((state: RootState) => state.auth);

  // Async dispatch
  const dispatch = useAppDispatch();

  // Passport token
  const [documentToken, setDocumentToken] = useState<string>('');

  const storedIdentityDocuments = coApplicant
    ? getCoApplicant(privateCustomer?.signers)?.person?.identity_documents
    : getMainApplicant(privateCustomer?.signers)?.person?.identity_documents ||
      privateCustomer?.person?.identity_documents;

  const idType =
    storedIdentityDocuments?.[0]?.type ?? IdentityDocumentType.NATIONAL_ID;

  // Document Error
  const [documentError, setDocumentError] = useState<string>('');
  const [showResidence, setShowResidence] = useState<boolean>(
    idType === IdentityDocumentType.PASSPORT,
  );
  const [identityChoice, setIdentityChoice] = useState<string>(
    idType ?? IdentityDocumentType.NATIONAL_ID,
  );
  const [documents, setDocuments] = useState<string[]>([]);

  // Employment type
  const employmentType = privateCustomer?.living_details?.employment_type;

  // Main Signer
  const mainSigner =
    getMainApplicant(activeApplication?.customer?.private?.signers) ||
    activeApplication?.customer?.private;

  // Co Signer
  const coSigner = getCoApplicant(
    activeApplication?.customer?.private?.signers,
  );

  // gets the resident document
  const findPassport = (identityDocuments?: IdentityDocuments[]) =>
    identityDocuments?.find(
      (doc) => doc.type === IdentityDocumentType.PASSPORT,
    );
  const residenceDoc =
    coApplicant && activeApplication?.customer?.private?.signers
      ? findPassport(coSigner?.person?.identity_documents)
      : findPassport(mainSigner?.person?.identity_documents) ||
        findPassport(
          activeApplication?.customer?.private?.person?.identity_documents,
        );

  const countIncomeDoc = getApplicantIncomeDocuments(
    activeApplication,
    coApplicant,
  ).length;

  const validationSchema = yup.object().shape({
    identity_choice: yup.string().required('Pflichtfeld'),
  });

  const {
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<{
    identity_choice: any;
  }>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      identity_choice: idType ?? IdentityDocumentType.NATIONAL_ID,
    },
  });

  useEffect(() => {
    const passport = privateCustomer?.person?.identity_documents
      ? privateCustomer?.person?.identity_documents[0]
      : '';

    if (passport && passport?.document_id) {
      setDocumentToken(passport.document_id || '');
    }
  }, [privateCustomer]);

  const getFirstLabel = () => {
    if (
      employmentType === 'EMPLOYMENT_TYPE_HOUSEWIFE' ||
      employmentType === 'EMPLOYMENT_TYPE_STUDENT'
    ) {
      return 'Dein Einkommensnachweis';
    }
    if (employmentType === 'EMPLOYMENT_TYPE_STATE_CIVIL_SERVANT') {
      return 'Dein aktueller Gehaltsbescheid';
    }
    if (employmentType === 'EMPLOYMENT_TYPE_PENSIONER') {
      return 'Dein aktueller Rentenbescheid';
    }
    if (employmentType === 'EMPLOYMENT_TYPE_JOB_SEEKER') {
      return 'Dein Einkommensnachweis, ggf. dein neuer Arbeitsvertrag';
    }
    if (employmentType === 'EMPLOYMENT_TYPE_SELF_EMPLOYED') {
      return (
        <>
          Dein Einkommensnachweis,
          <br />
          z.B. Einkommenssteuerbescheid, Jahresabschluß, BWA
        </>
      );
    }
    return 'Deine Einkommensnachweise';
  };

  const changeCheckbox =
    (identity_document_type: IdentityDocumentType) => () => {
      setIdentityChoice(identity_document_type);
      setValue('identity_choice', identity_document_type);
      if (identity_document_type === IdentityDocumentType.PASSPORT) {
        setShowResidence(true);
      } else {
        setShowResidence(false);
      }
    };

  const disableButton = (): boolean => {
    if (countIncomeDoc === 0) {
      return true;
    }

    if (identityChoice === IdentityDocumentType.PASSPORT && !residenceDoc) {
      return true;
    }
    return false;
  };

  // Form submit handler
  const formSubmitHandler = (
    values: DocumentDataSubmittedValues,
    unused: any,
    documentSpecificToken?: string,
    goNext = true,
  ) => {
    if (!documents?.includes(documentSpecificToken || documentToken)) {
      setDocuments([...documents, documentSpecificToken || documentToken]);
    }

    const data: DocumentDataSubmittedValues = {
      identity_choice: values.identity_choice,
      document_id: documentToken,
    };

    if (values.identity_choice === IdentityDocumentType.PASSPORT) {
      setDocumentError('');
      onSubmit(data, goNext, 'residence');
    }
    if (values.identity_choice === IdentityDocumentType.NATIONAL_ID) {
      const count = getDocumentsCount('PROOF_OF_RESIDENCE', coApplicant);
      const documentId = residenceDoc?.document_id || documentToken;
      if (count > 0) {
        dispatch(
          deleteCustomerDocument({
            accessToken,
            leaseAppId: activeApplication?.uuid || '',
            documentToken: documentId,
            documentType: 'PROOF_OF_RESIDENCE',
          }),
        );
      }
      onSubmit(data, goNext, 'residence');
    }
  };

  // Assign token once document get uploaded
  const assertToken = (token: string) => {
    setDocumentError('');
    setDocumentToken(token);
    const data = getValues();
    formSubmitHandler(data, '', token, false);
  };

  // Clear Token on Document Delete
  const clearToken = () => setDocumentToken('');

  return (
    <form
      onSubmit={handleSubmit(formSubmitHandler)}
      noValidate
      className='flex w-full flex-col gap-12'
    >
      <section className='flex flex-col gap-6'>
        <div className='flex flex-col gap-4'>
          <Headline variant='200' component='h2'>
            {getFirstLabel()}
          </Headline>
          <Text component='p' variant='100'>
            Du kannst hier alle deine Einkommensnachweise in einem oder in
            mehreren Dokumenten hochladen. Wir benötigen unter anderem die
            letzten drei Gehaltsnachweise so wie alle Nachweise weiterer
            Einkunftsquellen (z.B. Miete, Rente, Unterhalt, etc.).
          </Text>
        </div>
        <UploadBoxB2C
          coApplicant={coApplicant}
          fileName='Einkommensnachweise hinzufügen'
          documentType='PROOF_OF_INCOME'
          documentToken={documentToken}
          getToken={assertToken}
          removeToken={clearToken}
          setLoading={(value) =>
            value !== undefined && setLoading && setLoading(value)
          }
          isLoading={loading}
          error={documentError}
        />
      </section>

      <div className='flex flex-col gap-6'>
        <Headline variant='200' component='h2'>
          Dein Ausweisdokument
        </Headline>
        <FormRadioField
          name='identity_choice'
          label='Identifikation mit ...'
          row={false}
          value={identityChoice}
          onChange={(value) => changeCheckbox(value as IdentityDocumentType)()}
          errorMessage={errors?.identity_choice?.message as string}
          options={[
            {
              value: IdentityDocumentType.NATIONAL_ID,
              label: 'Deutschem Personalausweis',
            },
            {
              value: IdentityDocumentType.PASSPORT,
              label: 'Reisepass | Ausländischer Ausweis',
            },
          ]}
        />
        {showResidence && (
          <UploadBoxB2C
            coApplicant={coApplicant}
            fileName='Aktuelle Meldebestätigung'
            documentType='PROOF_OF_RESIDENCE'
            documentToken={documentToken}
            getToken={assertToken}
            removeToken={clearToken}
            setLoading={(value) =>
              value !== undefined && setLoading && setLoading(value)
            }
            error={documentError}
          />
        )}
        <Text component='p' variant='100'>
          Wenn Du keinen deutschen Personalausweis für die Identifikation
          verwenden möchtest, benötigen wir eine Kopie Deiner aktuellen
          Meldebescheinigung (maximal 6 Monate alt.)
        </Text>
      </div>

      <ButtonGroup
        type='back-next'
        className='back-next'
        buttonOneProps={{
          type: 'submit',
          loading,
          disabled: disableButton(),
          name: 'form-submit',
          dataTestId: 'next',
          children: 'nächster Schritt',
        }}
      />
    </form>
  );
};

export default DocumentTemplate;
